/* eslint-disable no-restricted-syntax */
/* eslint-disable no-continue */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import {
  Alert,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Space,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import BasicPriceTax, { BasicPriceTaxRef } from '../../../../../components/BasicPriceTax';
import BasicPricing, { BasicPricingData, BasicPricingRef } from '../../../../../components/BasicPricing';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../../components/BasicValueUnits';
import PageSection from '../../../../../components/core/PageSection/PageSection';
import { UnitOfMeasureType } from '../../../../../models/core/cache.type';
import { Action } from '../../../../../models/core/core.type';
import {
  EventDestinationQuotationType,
  EventEquipmentQuotationType,
  EventPackagingQuotationType,
  EventServiceQuotationType,
  EventTreatmentQuotationType,
  EventType,
  EventVehicleQuotationType,
} from '../../../../../models/core/events.type';
import eventsService from '../../../../../services/core/events.service';
import { Feature } from '../../../../../utils/constants.utils';
import { isNullOrEmpty } from '../../../../../utils/helper.utils';

type QuotationKind =
  | 'acondicionamento'
  | 'equipamento'
  | 'veiculo'
  | 'tratamento'
  | 'destinoFinal'
  | 'servico';

type QuotationType =
  | EventPackagingQuotationType
  | EventEquipmentQuotationType
  | EventVehicleQuotationType
  | EventTreatmentQuotationType
  | EventDestinationQuotationType
  | EventServiceQuotationType;

type QuotationEntry = {
  key: QuotationKind;
  title: string;
  quotation: QuotationType;
};

type Props = {
  opened: boolean;
  event: EventType;
  unitOfMeasures: UnitOfMeasureType[];
  zIndex: number;
  onClose(): void;
  onSaved(item: EventType): void;
};

type SectionResult = {
  key: QuotationKind;
  patch: {
    action: Action;
    preco: number | null;
    precificacao: any;
    [key: string]: any;
  };
};

type SectionRef = {
  validate(): SectionResult | null;
};

const tempByPassSupplierQuotationDefault = true;

const hasCustomerPricing = (quotation: QuotationType | null) => {
  return !isNullOrEmpty(quotation?.precificacao?.preco);
};

const buildQuotationSetupIssues = (kind: QuotationKind, quotation: QuotationType | null) => {
  if (!quotation) {
    return [];
  }

  const issues: string[] = [];

  if (isNullOrEmpty(quotation.idFornecedor)) {
    issues.push('fornecedor');
  }

  if (['acondicionamento', 'equipamento', 'servico'].includes(kind)) {
    if (isNullOrEmpty(quotation.quantidade)) {
      issues.push('quantidade da cotação');
    }
  }

  if (kind === 'veiculo') {
    if (isNullOrEmpty(quotation.quantidade)) {
      issues.push('quantidade da cotação');
    }
    if (isNullOrEmpty((quotation as EventVehicleQuotationType).frequenciaIdUnidadeMedida)) {
      issues.push('frequência da cotação');
    }
  }

  if (['tratamento', 'destinoFinal'].includes(kind)) {
    if (isNullOrEmpty(quotation.quantidade)) {
      issues.push('quantidade da cotação');
    }
    if (isNullOrEmpty((quotation as EventTreatmentQuotationType).quantidadeIdUnidadeMedida)) {
      issues.push('unidade da cotação');
    }
  }

  return issues;
};

const buildPendingQuotationEntries = (event: EventType): QuotationEntry[] => {
  const entries: QuotationEntry[] = [];

  const map: Array<{ key: QuotationKind; title: string; quotation: QuotationType | null }> = [
    {
      key: 'acondicionamento',
      title: 'Acondicionamento',
      quotation: event.acondicionamento?.cotacao || null,
    },
    { key: 'equipamento', title: 'Equipamento', quotation: event.equipamento?.cotacao || null },
    { key: 'veiculo', title: 'Veículo', quotation: event.veiculo?.cotacao || null },
    { key: 'tratamento', title: 'Tratamento', quotation: event.tratamento?.cotacao || null },
    { key: 'destinoFinal', title: 'Destino final', quotation: event.destinoFinal?.cotacao || null },
    { key: 'servico', title: 'Serviço', quotation: event.servico?.cotacao || null },
  ];

  for (const item of map) {
    if (!item.quotation) {
      continue;
    }

    const missingPricing = isNullOrEmpty(item.quotation.preco) || !hasCustomerPricing(item.quotation);
    const missingSetup = buildQuotationSetupIssues(item.key, item.quotation).length > 0;

    if (missingPricing || missingSetup) {
      entries.push(item as QuotationEntry);
    }
  }

  return entries;
};

export const hasPendingQuotationPricing = (event: EventType) => {
  return buildPendingQuotationEntries(event).length > 0;
};

const buildItemPlaceholder = (entry: QuotationEntry, event: EventType) => {
  if (entry.key === 'servico') {
    const quotation = entry.quotation as EventServiceQuotationType;
    const quotationSubItem = quotation.residuoAcondicionamento || quotation.residuoEquipamento;
    return (
      `${event.resumoJSON?.servico?.servico}${quotationSubItem ? ` - ${quotationSubItem}` : ''}` ||
      entry.title
    );
  }

  if (entry.key === 'acondicionamento') {
    const quotation = entry.quotation as EventPackagingQuotationType;
    return quotation.residuoAcondicionamento || entry.title;
  }

  if (entry.key === 'equipamento') {
    const quotation = entry.quotation as EventEquipmentQuotationType;
    return quotation.residuoEquipamento || entry.title;
  }

  if (entry.key === 'veiculo') {
    const quotation = entry.quotation as EventVehicleQuotationType;
    return quotation.residuoVeiculo || entry.title;
  }

  if (entry.key === 'tratamento') {
    const quotation = entry.quotation as EventTreatmentQuotationType;
    return quotation.residuoTratamento || entry.title;
  }

  if (entry.key === 'destinoFinal') {
    const quotation = entry.quotation as EventDestinationQuotationType;
    return quotation.residuoDestinoFinal || entry.title;
  }

  return entry.title;
};

const buildClientPlaceholder = (event: EventType) => {
  return (
    event.resumoJSON?.cliente?.clienteNomeFantasia ||
    event.resumoJSON?.cliente?.clienteRazaoSocial ||
    event.resumoJSON?.cliente?.clienteNome ||
    '-'
  );
};

const buildSupplierPlaceholder = (entry: QuotationEntry) => {
  return (
    entry.quotation.fornecedorNomeFantasia ||
    entry.quotation.fornecedorRazaoSocial ||
    entry.quotation.fornecedorNome ||
    '-'
  );
};

const buildUnitMeta = (unitOfMeasures: UnitOfMeasureType[], unitId: string | null | undefined) => {
  const parsedUnitId = unitId ? Number(unitId) : null;
  const unit = unitOfMeasures.find((x) => x.idUnidadeMedida === parsedUnitId);

  return {
    id: parsedUnitId,
    unidadeMedida: unit?.unidadeMedida || null,
    unidadeMedidaSigla: unit?.unidadeMedidaSigla || null,
  };
};

const buildPricingTypeFromQuotation = (
  unitOfMeasures: UnitOfMeasureType[],
  pricingData: BasicPricingData
) => {
  const quantidadeIdUnidadeMedida = pricingData.quantidadeIdUnidadeMedida
    ? Number(pricingData.quantidadeIdUnidadeMedida)
    : null;
  const frequenciaIdUnidadeMedida = pricingData.frequenciaIdUnidadeMedida
    ? Number(pricingData.frequenciaIdUnidadeMedida)
    : null;

  return {
    tipo: pricingData.tipoPrecificacao,
    quantidade: pricingData.quantidade,
    quantidadeIdUnidadeMedida,
    quantidadeUnidadeMedida:
      unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)?.unidadeMedida || null,
    quantidadeUnidadeMedidaSigla:
      unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)?.unidadeMedidaSigla || null,
    frequenciaIdUnidadeMedida,
    frequenciaUnidadeMedida:
      unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)?.unidadeMedida || null,
    frequenciaUnidadeMedidaSigla:
      unitOfMeasures.find((x) => x.idUnidadeMedida === frequenciaIdUnidadeMedida)?.unidadeMedidaSigla || null,
    preco: pricingData.preco,
    imposto: pricingData.imposto,
  };
};

const buildSupplierPatch = (
  kind: QuotationKind,
  unitOfMeasures: UnitOfMeasureType[],
  supplierValueUnitsData: BasicValueUnitsData
) => {
  if (kind === 'servico') {
    const unit1 = buildUnitMeta(unitOfMeasures, supplierValueUnitsData.idUnidade1);
    const unit2 = buildUnitMeta(unitOfMeasures, supplierValueUnitsData.idUnidade2);

    return {
      quantidade: supplierValueUnitsData.valor,
      frequencia: null,
      frequenciaIdUnidadeMedida1: unit1.id,
      frequenciaUnidadeMedida1: unit1.unidadeMedida,
      frequenciaUnidadeMedidaSigla1: unit1.unidadeMedidaSigla,
      frequenciaIdUnidadeMedida2: unit2.id,
      frequenciaUnidadeMedida2: unit2.unidadeMedida,
      frequenciaUnidadeMedidaSigla2: unit2.unidadeMedidaSigla,
    };
  }

  const quantityUnit = buildUnitMeta(unitOfMeasures, supplierValueUnitsData.idUnidade1);
  const frequencyUnit = buildUnitMeta(unitOfMeasures, supplierValueUnitsData.idUnidade2);
  const shouldKeepFrequency = ['acondicionamento', 'equipamento', 'veiculo'].includes(kind);

  return {
    quantidade: supplierValueUnitsData.valor,
    quantidadeIdUnidadeMedida: quantityUnit.id,
    quantidadeUnidadeMedida: quantityUnit.unidadeMedida,
    quantidadeUnidadeMedidaSigla: quantityUnit.unidadeMedidaSigla,
    frequenciaIdUnidadeMedida: shouldKeepFrequency ? frequencyUnit.id : null,
    frequenciaUnidadeMedida: shouldKeepFrequency ? frequencyUnit.unidadeMedida : null,
    frequenciaUnidadeMedidaSigla: shouldKeepFrequency ? frequencyUnit.unidadeMedidaSigla : null,
  };
};

const buildSupplierPatchFromCustomerPricing = (
  kind: QuotationKind,
  unitOfMeasures: UnitOfMeasureType[],
  customerPricing: {
    quantidade: number | null;
    quantidadeIdUnidadeMedida: number | null;
    frequenciaIdUnidadeMedida: number | null;
  }
) =>
  buildSupplierPatch(kind, unitOfMeasures, {
    valor: customerPricing.quantidade,
    idUnidade1: customerPricing.quantidadeIdUnidadeMedida?.toString() || null,
    idUnidade2: customerPricing.frequenciaIdUnidadeMedida?.toString() || null,
  });

const isSupplierPatchEmpty = (kind: QuotationKind, patch: { preco: number | null; [key: string]: any }) => {
  if (kind === 'servico') {
    return (
      isNullOrEmpty(patch.preco) &&
      isNullOrEmpty(patch.quantidade) &&
      isNullOrEmpty(patch.frequenciaIdUnidadeMedida1) &&
      isNullOrEmpty(patch.frequenciaIdUnidadeMedida2)
    );
  }

  return (
    isNullOrEmpty(patch.preco) &&
    isNullOrEmpty(patch.quantidade) &&
    isNullOrEmpty(patch.quantidadeIdUnidadeMedida) &&
    isNullOrEmpty(patch.frequenciaIdUnidadeMedida)
  );
};

const ensureAction = <T extends { action?: Action }>(item: T, action = Action.Nothing) => {
  if (!item) {
    return item;
  }

  return {
    ...item,
    action: item.action || action,
  };
};

const normalizeTolerances = <T extends { action?: Action }>(items: T[] | null | undefined) => {
  return (items || []).map((item) => ensureAction(item));
};

const normalizeQuotation = <T extends { action?: Action; tolerancias?: any[] }>(quotation: T | null) => {
  if (!quotation) {
    return quotation;
  }

  return {
    ...quotation,
    action: quotation.action || Action.Nothing,
    tolerancias: normalizeTolerances(quotation.tolerancias),
  };
};

const normalizeEventSection = <T extends { action?: Action; cotacao?: any }>(section: T | null) => {
  if (!section) {
    return section;
  }

  return {
    ...section,
    action: section.action || Action.Nothing,
    cotacao: normalizeQuotation(section.cotacao),
  };
};

const normalizeVehicleSection = <T extends { action?: Action; cotacao?: any; acondicionamentos?: any[] }>(
  section: T | null
) => {
  if (!section) {
    return section;
  }

  return {
    ...section,
    action: section.action || Action.Nothing,
    acondicionamentos: (section.acondicionamentos || []).map((item) => ensureAction(item)),
    cotacao: normalizeQuotation(section.cotacao),
  };
};

const normalizeDocuments = <T extends { action?: Action; arquivos?: any[] }>(
  items: T[] | null | undefined
) => {
  return (items || []).map((item) => ({
    ...item,
    action: item.action || Action.Nothing,
    arquivos: (item.arquivos || []).map((arquivo) => ensureAction(arquivo)),
  }));
};

const normalizeMeasurement = <T extends { action?: Action; movimentacoes?: any[] }>(
  measurement: T | null
) => {
  if (!measurement) {
    return measurement;
  }

  return {
    ...measurement,
    action: measurement.action || Action.Nothing,
    movimentacoes: (measurement.movimentacoes || []).map((item) => ensureAction(item)),
  };
};

const normalizeBilling = <T extends { action?: Action; relacoes?: any[] }>(billing: T | null) => {
  if (!billing) {
    return billing;
  }

  return {
    ...billing,
    action: billing.action || Action.Nothing,
    relacoes: (billing.relacoes || []).map((item) => ensureAction(item)),
  };
};

const normalizeEventActions = (event: EventType): EventType => {
  const baseEvent = { ...(event as EventType & { action?: Action }) };
  delete baseEvent.action;

  return {
    ...baseEvent,
    acondicionamento: normalizeEventSection(baseEvent.acondicionamento),
    equipamento: normalizeEventSection(baseEvent.equipamento),
    veiculo: normalizeVehicleSection(baseEvent.veiculo),
    tratamento: normalizeEventSection(baseEvent.tratamento),
    destinoFinal: normalizeEventSection(baseEvent.destinoFinal),
    servico: normalizeEventSection(baseEvent.servico),
    documentos: normalizeDocuments(baseEvent.documentos),
    medicao: normalizeMeasurement(baseEvent.medicao),
    faturamento: normalizeBilling(baseEvent.faturamento),
    relacoes: (baseEvent.relacoes || []).map((item) => ensureAction(item)),
  };
};

const QuotationPricingSection = forwardRef<
  SectionRef,
  {
    entry: QuotationEntry;
    event: EventType;
    unitOfMeasures: UnitOfMeasureType[];
    zIndex: number;
    disabled: boolean;
    tempByPassSupplierQuotation: boolean;
    onToggleTempByPassSupplierQuotation(value: boolean): void;
  }
>(
  (
    {
      entry,
      event,
      unitOfMeasures,
      zIndex,
      disabled,
      tempByPassSupplierQuotation,
      onToggleTempByPassSupplierQuotation,
    },
    ref
  ) => {
    const refPricingForm = useRef<BasicPricingRef>(null);
    const refSupplierValueUnits = useRef<BasicValueUnitsRef>(null);
    const refSupplierPriceTax = useRef<BasicPriceTaxRef>(null);

    const quantityUnitOptionsRaw = useMemo(() => {
      if (['acondicionamento', 'equipamento'].includes(entry.key)) {
        return unitOfMeasures
          .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
          .map((x) => ({
            value: x.idUnidadeMedida.toString(),
            label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
            group: x.tipo,
          }));
      }

      if (['veiculo', 'tratamento', 'destinoFinal'].includes(entry.key)) {
        return unitOfMeasures
          .filter(
            (x) =>
              x.tipo === 'Massa' ||
              x.tipo === 'Volume' ||
              (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
          )
          .map((x) => ({
            value: x.idUnidadeMedida.toString(),
            label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
            group: x.tipo,
          }));
      }

      return unitOfMeasures
        .filter((x) => x.tipo !== 'Tempo')
        .map((x) => ({
          value: x.idUnidadeMedida.toString(),
          label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
          group: x.tipo,
        }));
    }, [entry.key, unitOfMeasures]);

    const frequencyUnitOptionsRaw = useMemo(() => {
      if (entry.key === 'veiculo') {
        return unitOfMeasures
          .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'col')
          .map((x) => ({
            value: x.idUnidadeMedida.toString(),
            label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
            group: x.tipo,
          }));
      }

      if (['tratamento', 'destinoFinal'].includes(entry.key)) {
        return [];
      }

      return unitOfMeasures
        .filter(
          (x) =>
            (x.tipo === 'Tempo' &&
              (x.unidadeMedidaSigla === 'd' ||
                x.unidadeMedidaSigla === 'sem' ||
                x.unidadeMedidaSigla === 'mês' ||
                x.unidadeMedidaSigla === 'ano')) ||
            (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
        )
        .map((x) => ({
          value: x.idUnidadeMedida.toString(),
          label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
          group: x.tipo,
        }));
    }, [entry.key, unitOfMeasures]);

    const withCustomerFrequency = !['tratamento', 'destinoFinal'].includes(entry.key);
    const disableCustomerQuantityUnit = ['acondicionamento', 'equipamento'].includes(entry.key);
    const showSupplierFrequency = !['tratamento', 'destinoFinal'].includes(entry.key);
    const disableSupplierQuantityUnit = ['acondicionamento', 'equipamento'].includes(entry.key);
    const disableSupplierFrequencyUnit = entry.key === 'veiculo';

    useImperativeHandle(ref, () => ({
      validate() {
        const pricingData = refPricingForm.current?.validate();
        const supplierValueUnitsData = refSupplierValueUnits.current?.validate();
        const supplierPriceTaxData = refSupplierPriceTax.current?.validate();

        if (!pricingData || !supplierValueUnitsData || !supplierPriceTaxData) {
          return null;
        }

        const customerPricing = buildPricingTypeFromQuotation(unitOfMeasures, pricingData);
        const supplierPatch = {
          action: Action.Modify,
          ...buildSupplierPatch(entry.key, unitOfMeasures, supplierValueUnitsData),
          preco:
            tempByPassSupplierQuotation && isNullOrEmpty(supplierPriceTaxData.preco)
              ? 0
              : supplierPriceTaxData.preco,
          precificacao: customerPricing,
        };

        if (tempByPassSupplierQuotation && isSupplierPatchEmpty(entry.key, supplierPatch)) {
          return {
            key: entry.key,
            patch: {
              action: Action.Modify,
              ...buildSupplierPatchFromCustomerPricing(entry.key, unitOfMeasures, customerPricing),
              preco: 0,
              precificacao: customerPricing,
            },
          };
        }

        return {
          key: entry.key,
          patch: supplierPatch,
        };
      },
    }));

    return (
      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between">
          <PageSection
            size="md"
            color={Feature.Home.Event.color}
            label={entry.title}
            text="Preencha a cotação completa para seguir com a medição."
          />
          <Text size="sm" c="dimmed" fs="italic">
            {buildItemPlaceholder(entry, event)}
          </Text>
        </Group>
        <Space h="xs" />
        <SimpleGrid cols={1} spacing="md">
          <Paper shadow="xs" p="md" withBorder>
            <Group justify="space-between">
              <PageSection
                size="md"
                color={Feature.Home.Event.color}
                label="Cliente"
                text="Informações operacionais da cotação com o cliente."
              />
              <Text size="sm" c="dimmed" fs="italic">
                {buildClientPlaceholder(event)}
              </Text>
            </Group>
            <Space h="xs" />
            <BasicPricing
              ref={refPricingForm}
              isEdit
              disabled={disabled}
              frequencyUnitDisabled={disableSupplierFrequencyUnit}
              value={{
                tipoPrecificacao: 'E' as any,
                quantidade: entry.quotation.precificacao?.quantidade ?? null,
                quantidadeIdUnidadeMedida:
                  entry.quotation.precificacao?.quantidadeIdUnidadeMedida?.toString() || null,
                frequenciaIdUnidadeMedida:
                  entry.quotation.precificacao?.frequenciaIdUnidadeMedida?.toString() || null,
                preco: entry.quotation.precificacao?.preco ?? null,
                imposto: entry.quotation.precificacao?.imposto ?? null,
              }}
              quantityUnitOptions={quantityUnitOptionsRaw as any}
              frequencyUnitOptions={frequencyUnitOptionsRaw as any}
              withFrequency={withCustomerFrequency}
              quantityUnitDisabled={disableCustomerQuantityUnit}
              hideTipoPrecificacaoSelector
              forcePricingActive
              zIndex={zIndex + 2}
            />
          </Paper>
          <Paper shadow="xs" p="md" withBorder>
            <Group justify="space-between" align="flex-start">
              <Group gap="md" align="center">
                <PageSection
                  size="md"
                  color={Feature.Home.Event.color}
                  label="Fornecedor"
                  text="Informações operacionais da cotação com o fornecedor."
                />
                <Switch
                  checked={tempByPassSupplierQuotation}
                  disabled={disabled}
                  label="Usar dados do cliente"
                  onChange={(event2) => onToggleTempByPassSupplierQuotation(event2.currentTarget.checked)}
                />
              </Group>
              <Text size="sm" c="dimmed" fs="italic">
                {buildSupplierPlaceholder(entry)}
              </Text>
            </Group>
            <Space h="xs" />
            {tempByPassSupplierQuotation && (
              <>
                <Alert color="blue" variant="light">
                  Caso não preenchido, as mesmas unidades/frequência do cliente serão copiadas com preço igual
                  a R$ 0,00.
                </Alert>
                <Space h="xs" />
              </>
            )}
            <Paper shadow="xs" p="md" withBorder>
              <Grid columns={4}>
                <Grid.Col span={3}>
                  <BasicValueUnits
                    ref={refSupplierValueUnits}
                    isEdit
                    label="Quantidade/Frequência"
                    showUnit2={showSupplierFrequency}
                    disableUnit1={disableSupplierQuantityUnit}
                    disableUnit2={disableSupplierFrequencyUnit}
                    validationMode={tempByPassSupplierQuotation ? 'all-or-none' : 'all'}
                    value={{
                      valor: entry.quotation.quantidade ?? null,
                      idUnidade1:
                        entry.key === 'servico'
                          ? (
                              entry.quotation as EventServiceQuotationType
                            ).frequenciaIdUnidadeMedida1?.toString() || null
                          : (
                              entry.quotation as
                                | EventPackagingQuotationType
                                | EventEquipmentQuotationType
                                | EventVehicleQuotationType
                                | EventTreatmentQuotationType
                                | EventDestinationQuotationType
                            ).quantidadeIdUnidadeMedida?.toString() || null,
                      idUnidade2:
                        entry.key === 'servico'
                          ? (
                              entry.quotation as EventServiceQuotationType
                            ).frequenciaIdUnidadeMedida2?.toString() || null
                          : ['acondicionamento', 'equipamento', 'veiculo'].includes(entry.key)
                          ? (
                              entry.quotation as
                                | EventPackagingQuotationType
                                | EventEquipmentQuotationType
                                | EventVehicleQuotationType
                            ).frequenciaIdUnidadeMedida?.toString() || null
                          : null,
                    }}
                    unit1Options={quantityUnitOptionsRaw as any}
                    unit2Options={frequencyUnitOptionsRaw as any}
                    zIndex={zIndex + 2}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <BasicPriceTax
                    ref={refSupplierPriceTax}
                    isEdit
                    disabled={disabled}
                    requiredPrice={!tempByPassSupplierQuotation}
                    disabledTax
                    showTax={false}
                    labelPreco="Preço"
                    value={{
                      preco: entry.quotation.preco ?? null,
                      imposto: null,
                    }}
                    zIndex={zIndex + 2}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Paper>
        </SimpleGrid>
      </Paper>
    );
  }
);

const applyQuotationPatch = (event: EventType, result: SectionResult): EventType => {
  const baseEvent = normalizeEventActions(event);

  switch (result.key) {
    case 'acondicionamento':
      return {
        ...baseEvent,
        acondicionamento: baseEvent.acondicionamento
          ? {
              ...baseEvent.acondicionamento,
              action: Action.Nothing,
              cotacao: { ...baseEvent.acondicionamento.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.acondicionamento,
      };
    case 'equipamento':
      return {
        ...baseEvent,
        equipamento: baseEvent.equipamento
          ? {
              ...baseEvent.equipamento,
              action: Action.Nothing,
              cotacao: { ...baseEvent.equipamento.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.equipamento,
      };
    case 'veiculo':
      return {
        ...baseEvent,
        veiculo: baseEvent.veiculo
          ? {
              ...baseEvent.veiculo,
              action: Action.Nothing,
              cotacao: { ...baseEvent.veiculo.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.veiculo,
      };
    case 'tratamento':
      return {
        ...baseEvent,
        tratamento: baseEvent.tratamento
          ? {
              ...baseEvent.tratamento,
              action: Action.Nothing,
              cotacao: { ...baseEvent.tratamento.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.tratamento,
      };
    case 'destinoFinal':
      return {
        ...baseEvent,
        destinoFinal: baseEvent.destinoFinal
          ? {
              ...baseEvent.destinoFinal,
              action: Action.Nothing,
              cotacao: { ...baseEvent.destinoFinal.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.destinoFinal,
      };
    case 'servico':
      return {
        ...baseEvent,
        servico: baseEvent.servico
          ? {
              ...baseEvent.servico,
              action: Action.Nothing,
              cotacao: { ...baseEvent.servico.cotacao, ...result.patch, action: Action.Modify },
            }
          : baseEvent.servico,
      };
    default:
      return baseEvent;
  }
};

export default function QuotationPricingModal(props: Props) {
  const [updating, setUpdating] = useState(false);
  const [tempByPassSupplierQuotation, setTempByPassSupplierQuotation] = useState(
    tempByPassSupplierQuotationDefault
  );
  const sectionRefs = useRef<Partial<Record<QuotationKind, SectionRef | null>>>({});

  const entries = useMemo(() => buildPendingQuotationEntries(props.event), [props.event]);

  const handleSave = async () => {
    const results: SectionResult[] = [];

    for (const entry of entries) {
      const result = sectionRefs.current[entry.key]?.validate();
      if (!result) {
        return;
      }

      results.push(result);
    }

    try {
      setUpdating(true);

      let nextEvent = props.event;
      for (const result of results) {
        nextEvent = applyQuotationPatch(nextEvent, result);
      }

      const response = await eventsService.update(nextEvent as any);
      props.onSaved(response);
      props.onClose();

      showNotification({
        title: 'Evento - Visualizar',
        message: results.length > 1 ? 'Cotações atualizadas com sucesso.' : 'Cotação atualizada com sucesso.',
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Evento - Visualizar',
        message: error?.isBusinessException ? error.description : 'Não foi possível atualizar as cotações.',
        color: 'red',
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      size="60%"
      title={`Definir preços${entries.length > 1 ? ` (${entries.length} cotações)` : ''}`}
      zIndex={props.zIndex + 1}
    >
      <Paper shadow="xs" p="md" withBorder>
        <Stack gap="md">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Defina os preços antes de calcular"
            color="yellow"
          >
            Preencha os precos do cliente antes de calcular ou definir a medicao. O fornecedor pode usar os
            dados do cliente com preco zero quando a opcao estiver ativa.
          </Alert>

          {entries.map((entry) => (
            <QuotationPricingSection
              key={entry.key}
              ref={(ref) => {
                sectionRefs.current[entry.key] = ref;
              }}
              entry={entry}
              unitOfMeasures={props.unitOfMeasures}
              zIndex={props.zIndex}
              disabled={updating}
              event={props.event}
              tempByPassSupplierQuotation={tempByPassSupplierQuotation}
              onToggleTempByPassSupplierQuotation={setTempByPassSupplierQuotation}
            />
          ))}

          <Group justify="right">
            <Button color="secondary" disabled={updating} onClick={props.onClose}>
              Fechar
            </Button>
            <Button color="primary" loading={updating} onClick={handleSave}>
              Salvar cotações
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Modal>
  );
}
